import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class SearchDropdownComponent extends Component {
  requiredRefs = ['searchDropdown'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    this.addEventListener('scroll', this.#onScrollCapture, { capture: true, passive: true });
    window.addEventListener('resize', this.#syncResourceCarouselLeftFade, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.removeEventListener('scroll', this.#onScrollCapture, { capture: true });
    window.removeEventListener('resize', this.#syncResourceCarouselLeftFade);
  }

  /**
   * Shows a left-edge shadow on predictive resource carousels when scrolled (desktop dropdown).
   * @param {Event} event
   */
  #onScrollCapture = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || target.tagName !== 'SLIDESHOW-SLIDES') return;

    const slideshow = target.closest('slideshow-component');
    if (
      !slideshow ||
      !slideshow.classList.contains('predictive-search-results__wrapper') ||
      !this.contains(slideshow)
    ) {
      return;
    }

    this.#updateResourceCarouselLeftFade(/** @type {HTMLElement} */ (target), slideshow);
  };

  #syncResourceCarouselLeftFade = () => {
    const searchDropdown = /** @type {HTMLElement | undefined} */ (this.refs.searchDropdown);
    if (!searchDropdown?.classList.contains('search-dropdown--open')) return;

    const slides = this.querySelectorAll(
      'slideshow-component.predictive-search-results__wrapper slideshow-slides'
    );
    slides.forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const slideshow = el.closest('slideshow-component');
      if (slideshow) this.#updateResourceCarouselLeftFade(el, slideshow);
    });
  };

  /**
   * @param {HTMLElement} slidesEl
   * @param {Element} slideshow
   */
  #updateResourceCarouselLeftFade(slidesEl, slideshow) {
    const showLeftFade = slidesEl.scrollLeft > 2;
    slideshow.classList.toggle('scroll-fade--left', showLeftFade);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDropdown();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDropdown() {
    const searchDropdown = /** @type {HTMLElement} */ (this.refs.searchDropdown);

    if (searchDropdown?.classList.contains('search-dropdown--open')) return;

    searchDropdown?.classList.add('search-dropdown--open');

    setTimeout(() => {
      const input = /** @type {HTMLInputElement | null} */ (this.querySelector('input.search-input'));
      input?.focus();
    }, 50);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#syncResourceCarouselLeftFade());
    });
  }

  /**
   * Closes the dialog.
   */
  closeDropdown = async () => {
    const searchDropdown = /** @type {HTMLElement} */ (this.refs.searchDropdown);

    if (!searchDropdown?.classList.contains('search-dropdown--open')) return;

    searchDropdown?.classList.remove('search-dropdown--open');

    this.dispatchEvent(new SearchDropdownCloseEvent());
  };


  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDropdown();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDropdown();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('search-dropdown')) customElements.define('search-dropdown', SearchDropdownComponent);

export class SearchDropdownCloseEvent extends CustomEvent {
  constructor() {
    super(SearchDropdownCloseEvent.eventName);
  }

  static eventName = 'search-dropdown:close';
}
